import { Queue } from '@nexelem/graphne-api';
import { groupBy } from 'lodash';
import { createSelector } from 'reselect';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { swap } from './utils';
import { umStorage } from '@nexelem/services';

export type QueueStored = Queue & {
  isHidden?: boolean;
  index: number;
};

export type QueueGroup = {
  id: number;
  name: string;
  hex: string;
  index: number;
  isHidden?: boolean;
  queues: Array<QueueStored>;
};

type State = {
  draftGroups: QueueGroup[];
  displayGroups: QueueGroup[];
  setQueues: (queues: Queue[]) => void;
  moveGroup: (activeId: number, overId: number) => void;
  moveQueue: (groupId: number, activeId: number, overId: number) => void;
  showAll: () => void;
  hideAll: () => void;
  toggleQueueDraftVisibility: (groupId: number, queueId: number) => void;
  toggleGroupDraftVisibility: (groupId: number) => void;
  applyDraftChanges: () => void;
};

export const useGroupedQueuesStore = create(
  persist(
    immer<State>((set, get) => {
      return {
        draftGroups: [],
        displayGroups: [],
        setQueues: (queues) => {
          const previousGroups = get().displayGroups;

          const groupedData = Object.values(groupBy(queues, ({ area }) => area.id)).map((groupedQueues) => {
            const currentGroup = groupedQueues[0]?.area;
            const previousGroup = currentGroup && previousGroups.find((group) => group.id === currentGroup.id);

            const indexedQueues = groupedQueues.map((queue) => {
              const previousQueue = previousGroup?.queues.find((prevQueue) => prevQueue.id === queue.id);
              return {
                ...queue,
                index: previousQueue?.index,
                isHidden: previousQueue?.isHidden,
              };
            });

            return {
              id: currentGroup?.id as number,
              name: currentGroup?.name as string,
              hex: currentGroup?.hex as string,
              index: previousGroup?.index,
              isHidden: previousGroup?.isHidden,
              queues: indexedQueues,
            };
          });

          // find indexed elements and sort them.
          const sortedIndexedGroups = groupedData
            .filter((group) => group.index !== undefined)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .toSorted((a, b) => a.index! - b.index!);
          // find rest elements, should appear at the end of the list
          const notIndexedGroups = groupedData.filter((group) => group.index === undefined);

          // concat all elements, add indexes to be always defined
          const allGroups = [...sortedIndexedGroups, ...notIndexedGroups].map((group, index) => {
            return { ...group, index };
          });

          const groupsWithSortedQueues = allGroups.map((group) => {
            const sortedIndexedQueues = group.queues
              .filter((queue) => queue.index !== undefined)
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              .toSorted((a, b) => a.index! - b.index!);
            const notIndexedQueues = group.queues.filter((queue) => queue.index === undefined);

            const allQueues = [...sortedIndexedQueues, ...notIndexedQueues].map((queue, index) => {
              return { ...queue, index };
            });

            return {
              ...group,
              queues: allQueues,
            };
          });

          set((state) => {
            state.displayGroups = groupsWithSortedQueues;
            state.draftGroups = groupsWithSortedQueues;
          });
        },
        moveGroup: (activeId, overId) => {
          const groups = get().draftGroups;

          const swappedArray = swap(groups, activeId, overId);

          set((state) => {
            state.draftGroups = swappedArray;
          });
        },
        moveQueue: (groupId, activeId, overId) => {
          const queues = get().draftGroups.find(({ id }) => id === groupId)?.queues ?? [];

          const swappedArray = swap(queues, activeId, overId);

          set((state) => {
            state.draftGroups = state.draftGroups.map((group) => {
              if (group.id === groupId) {
                return { ...group, queues: swappedArray };
              }
              return group;
            });
          });
        },
        showAll: () => {
          set((state) => {
            state.displayGroups = state.displayGroups.map((group) => {
              return {
                ...group,
                isHidden: false,
                queues: group.queues.map((queue) => {
                  return { ...queue, isHidden: false };
                }),
              };
            });
            state.draftGroups = state.displayGroups;
          });
        },
        hideAll: () => {
          set((state) => {
            state.displayGroups = state.displayGroups.map((group) => {
              return {
                ...group,
                isHidden: true,
                queues: group.queues.map((queue) => {
                  return { ...queue, isHidden: true };
                }),
              };
            });
            state.draftGroups = state.displayGroups;
          });
        },
        toggleQueueDraftVisibility: (groupId, queueId) => {
          set((state) => {
            state.draftGroups = state.draftGroups.map((group) => {
              if (group.id === groupId) {
                const updatedQueues = group.queues.map((queue) => {
                  if (queue.id === queueId) {
                    return { ...queue, isHidden: !queue.isHidden };
                  }
                  return queue;
                });

                const allQueuesHidden = updatedQueues.every((queue) => queue.isHidden);

                return {
                  ...group,
                  isHidden: allQueuesHidden,
                  queues: updatedQueues,
                };
              }
              return group;
            });
          });
        },
        toggleGroupDraftVisibility: (groupId) => {
          set((state) => {
            state.draftGroups = state.draftGroups.map((group) => {
              if (group.id === groupId) {
                return {
                  ...group,
                  isHidden: !group.isHidden,
                  queues: group.queues.map((queue) => {
                    return { ...queue, isHidden: !group.isHidden };
                  }),
                };
              }
              return group;
            });
          });
        },
        applyDraftChanges: () => {
          set((state) => {
            state.displayGroups = state.draftGroups;
          });
        },
      };
    }),
    {
      name: 'grouped-queues-store',
      storage: createJSONStorage(() => umStorage),
      // do not hydrate the store when user is not logged in, hydration is handled in Hydration.tsx component
      skipHydration: true,
      partialize: (state) => {
        return {
          displayGroups: state.displayGroups,
        };
      },
    },
  ),
);

export const visibleQueuesSelector = createSelector(
  (state: State) => state.displayGroups,
  (displayGroups) =>
    displayGroups
      .filter(({ isHidden }) => !isHidden)
      .flatMap(({ queues }) => queues)
      .filter(({ isHidden }) => !isHidden),
);

export const visibleQueuesCountSelector = (queueId: number) => (state: State) =>
  state.displayGroups.find(({ id }) => id === queueId)?.queues.filter(({ isHidden }) => !isHidden).length;

export const queueByIdSelector = (queueId: number) => (state: State) =>
  state.displayGroups.flatMap(({ queues }) => queues).find(({ id }) => id === queueId);
